import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Save,
  Unplug,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'
import { apiClient } from '@/infrastructure/api/client'
import { useGetBotSettings, useUpdateBotSettings } from '@/application/services/settings.service'

// ──────────────────────────────────────────────
// YouTube token-status shape (server response)
// ──────────────────────────────────────────────
interface YoutubeTokenStatus {
  isConnected: boolean
  botChannelTitle?: string
  botChannelId?: string
  expiresAt?: string
  expiresInMinutes?: number
  needsReauth: boolean
  scopes?: string[]
}

export function YoutubeBotPanel() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<YoutubeTokenStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [revoking, setRevoking] = useState(false)
  const [connecting, setConnecting] = useState(false)

  const { data: settings, isLoading: settingsLoading } = useGetBotSettings()
  const updateSettings = useUpdateBotSettings()
  const [liveId, setLiveId] = useState('')

  useEffect(() => {
    if (settings) {
      setLiveId(settings.youtubeLiveId ?? '')
    }
  }, [settings])

  const fetchStatus = useCallback(async () => {
    try {
      const result = await apiClient.get<YoutubeTokenStatus>('/auth/youtube/status')
      setStatus(result.data)
    } catch {
      setStatus({ isConnected: false, needsReauth: true })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const result = await apiClient.get<{ url: string }>('/auth/youtube/url')
      window.location.href = result.data.url
    } catch (error: unknown) {
      toast.error(
        `Could not start OAuth flow: ${error instanceof Error ? error.message : String(error)}`,
      )
      setConnecting(false)
    }
  }

  const handleRevoke = async () => {
    if (!window.confirm('Disconnect the bot and revoke its YouTube tokens?')) return
    setRevoking(true)
    try {
      await apiClient.delete('/auth/youtube')
      toast.success('Bot disconnected.')
      await fetchStatus()
    } catch (error: unknown) {
      toast.error(`Failed to revoke: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setRevoking(false)
    }
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ youtubeLiveId: liveId || null })
      toast.success('YouTube Live Video ID saved.')
    } catch {
      toast.error('Failed to save YouTube Live Video ID.')
    }
  }

  if (loading || settingsLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin" />
        {t('admin.youtube_checking')}
      </div>
    )
  }

  const isOk = status?.isConnected && !status.needsReauth

  return (
    <div className="space-y-4">
      {/* Connection status row */}
      <div className="flex flex-wrap items-center gap-3">
        {isOk ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
            <span className="text-sm font-medium">
              {t('admin.youtube_connected_as')}{' '}
              <span className="font-bold text-foreground">{status!.botChannelTitle}</span>
            </span>
            <Badge variant="secondary" className="text-xs">
              {t('admin.youtube_expires_in', { minutes: status!.expiresInMinutes })}
            </Badge>
          </>
        ) : status?.botChannelTitle ? (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
              {t('admin.youtube_expired', { username: status.botChannelTitle })}
            </span>
          </>
        ) : (
          <>
            <XCircle className="w-5 h-5 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground">
              {t('admin.youtube_not_connected')}
            </span>
          </>
        )}
      </div>

      {/* Scopes (only when connected) */}
      {isOk && status?.scopes && (
        <div className="flex flex-wrap gap-1">
          {status.scopes.map((scope) => (
            <Badge key={scope} variant="outline" className="text-xs font-mono">
              {scope}
            </Badge>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {isOk ? (
          <Button
            variant="destructive"
            size="sm"
            leftIcon={<Unplug className="w-4 h-4" />}
            onClick={handleRevoke}
            isLoading={revoking}
          >
            {t('admin.youtube_disconnect')}
          </Button>
        ) : (
          <Button
            size="sm"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            onClick={handleConnect}
            isLoading={connecting}
          >
            {status?.needsReauth && status.botChannelTitle
              ? t('admin.youtube_reauthenticate')
              : t('admin.youtube_connect')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={fetchStatus}
        >
          Refresh
        </Button>
      </div>

      {/* YouTube Live Video ID input */}
      <div className="space-y-2 pt-2 border-t border-border">
        <label className="text-sm font-medium">YouTube Video ID</label>
        <div className="flex gap-2">
          <Input
            value={liveId}
            onChange={(event) => setLiveId(event.target.value)}
            placeholder="e.g. dQw4w9WgXcQ"
            className="flex-1"
          />
          <Button
            onClick={handleSave}
            isLoading={updateSettings.isPending}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter the ID of the YouTube video (the part after "v=" in the URL). The bot will
          automatically retrieve the associated Live Chat ID.
        </p>
      </div>
    </div>
  )
}
